import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { apiClient } from '@/api/axiosClient';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import SearchableSelect from '@/components/common/SearchableSelect';
import SearchableMultiSelect from '@/components/common/SearchableMultiSelect';
import { INDIAN_STATES, stateNameForCode } from '@/lib/indianStates';
import type { ClientDto, CustomerDto } from '@/types/materials';
import type { BranchDto } from '@/types/organization';

function buildSchema(t: (key: string) => string) {
  return z.object({
    code: z.string().min(1, t('form.validation.codeRequired')),
    name: z.string().min(1, t('form.validation.nameRequired')),
    gstin: z.string().nullable(),
    address: z.string().nullable(),
    contactPerson: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    status: z.enum(['Active', 'Inactive']),
    stateCode: z.string().nullable(),
    stateName: z.string().nullable(),
    billingContactName: z.string().nullable(),
    billingContactEmail: z.string().nullable(),
    billingContactPhone: z.string().nullable(),
    creditTermDays: z.coerce.number().nullable(),
    primaryBranchId: z.string().nullable(),
    specialHandlingInstructions: z.string().nullable(),
    customerIds: z.array(z.string()),
  });
}

export type ClientFormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: ClientFormValues = {
  code: '', name: '', gstin: null, address: null, contactPerson: null, phone: null, email: null, status: 'Active',
  stateCode: null, stateName: null,
  billingContactName: null, billingContactEmail: null, billingContactPhone: null, creditTermDays: null,
  primaryBranchId: null, specialHandlingInstructions: null, customerIds: [],
};

interface ClientFormDialogProps {
  open: boolean;
  client: ClientDto | null;
  onClose: () => void;
  onSubmit: (values: ClientFormValues) => void;
  loading?: boolean;
}

export default function ClientFormDialog({ open, client, onClose, onSubmit, loading }: ClientFormDialogProps) {
  const { t } = useTranslation('clientMaster');
  const schema = buildSchema(t);
  const [tab, setTab] = useState(0);

  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => (await apiClient.get<BranchDto[]>('/branches')).data,
    enabled: open,
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => (await apiClient.get<CustomerDto[]>('/customers')).data,
    enabled: open,
  });

  const { control, handleSubmit, reset, setValue } = useForm<ClientFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      setTab(0);
      reset(
        client
          ? {
              code: client.code, name: client.name, gstin: client.gstin, address: client.address,
              contactPerson: client.contactPerson, phone: client.phone, email: client.email,
              status: client.status as 'Active' | 'Inactive',
              stateCode: client.stateCode, stateName: client.stateName,
              billingContactName: client.billingContactName, billingContactEmail: client.billingContactEmail,
              billingContactPhone: client.billingContactPhone, creditTermDays: client.creditTermDays,
              primaryBranchId: client.primaryBranchId, specialHandlingInstructions: client.specialHandlingInstructions,
              customerIds: client.customerIds,
            }
          : DEFAULTS,
      );
    }
  }, [open, client, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{client ? client.name : t('addClient')}</DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t('tabs.general')} />
        <Tab label={t('tabs.billing3pl')} />
      </Tabs>
      <DialogContent>
        {tab === 0 && (
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={1}>
            <Controller
              name="code"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.code')} fullWidth disabled={Boolean(client)} error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.name')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="gstin" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.gstin')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="contactPerson" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.contactPerson')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="phone" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.phone')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="email" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.email')} fullWidth type="email" />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="stateCode"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  label={t('form.state')}
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const code = e.target.value || null;
                    field.onChange(code);
                    setValue('stateName', stateNameForCode(code));
                  }}
                >
                  <MenuItem value="">{t('form.stateNone')}</MenuItem>
                  {INDIAN_STATES.map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </GridItem>
          <GridItem xs={2}>
            <Controller
              name="address"
              control={control}
              render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.address')} fullWidth multiline rows={2} />}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label={t('form.status')} fullWidth>
                  <MenuItem value="Active">{t('form.statusActive')}</MenuItem>
                  <MenuItem value="Inactive">{t('form.statusInactive')}</MenuItem>
                </TextField>
              )}
            />
          </GridItem>
        </GridContainer>
        )}

        {tab === 1 && (
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={1}>
            <Controller
              name="billingContactName"
              control={control}
              render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.billingContactName')} fullWidth sx={{ mt: 1 }} />}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="billingContactEmail"
              control={control}
              render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.billingContactEmail')} fullWidth type="email" sx={{ mt: 1 }} />}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="billingContactPhone"
              control={control}
              render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.billingContactPhone')} fullWidth />}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="creditTermDays"
              control={control}
              render={({ field }) => (
                <TextField {...field} value={field.value ?? ''} type="number" label={t('form.creditTermDays')} helperText={t('form.creditTermDaysHelper')} fullWidth />
              )}
            />
          </GridItem>
          <GridItem xs={2}>
            <Controller
              name="primaryBranchId"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  label={t('form.primaryBranch')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                  helperText={t('form.primaryBranchHelper')}
                  options={[{ value: '', label: t('form.primaryBranchNone') }, ...branches.map((b) => ({ value: b.id, label: b.name }))]}
                />
              )}
            />
          </GridItem>
          <GridItem xs={2}>
            <Controller
              name="specialHandlingInstructions"
              control={control}
              render={({ field }) => (
                <TextField {...field} value={field.value ?? ''} label={t('form.specialHandlingInstructions')} fullWidth multiline rows={2} />
              )}
            />
          </GridItem>
          <GridItem xs={2}>
            <Controller
              name="customerIds"
              control={control}
              render={({ field }) => (
                <SearchableMultiSelect
                  label={t('form.mappedCustomers')} value={field.value} onChange={field.onChange} fullWidth
                  helperText={t('form.mappedCustomersHelper')}
                  options={customers.map((c) => ({ value: c.id, label: c.name }))}
                />
              )}
            />
          </GridItem>
        </GridContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('discardChanges')}</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          {t('saveClient')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
